import { EditableCallbackParams } from 'ag-grid-community'
import {
  CollateralBasketType,
  CollectionType,
  CounterpartyInstruction,
  NegotiationFieldMeta,
  NestedLegItem,
  Order,
  OrderDetail,
  OrderDetailSummary,
  OrderSummary,
  RepoType,
  SettlementVenue,
  SummaryRowDirection,
  TradingType
} from 'api'
import { Locale, Translation } from 'locale'
import { isEmpty, isNil, startCase } from 'lodash'
import { isAonOrOcoCollection, TradingEntityTypeUtils, TradingEntityWithAliasHelper } from 'model'
import { TypedEditableCallbackParams } from 'view/components/datagrid'
import {
  isOrderDetail,
  isOrderSummary,
  isSmartGroupCollection,
  isSmartGroupOrderDetail,
  isSmartGroupOrderDetailSummary
} from 'view/routes/requests/shared/utils'
import { stagingRowDataGetter } from '../groupRowFormatters'
import { makeCanEditPreTradeAssignmentDetailField } from './common'

export const isChildRow = (params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) => !params.node.group

export function canEditRfqType(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { node } = params
  const isGroupRow = !!node.group

  const rowData = stagingRowDataGetter(params)

  if (!rowData) {
    return false
  }

  if (rowData.collection?.type === CollectionType.ALL_OR_NOTHING) {
    return isGroupRow
  }

  return !isGroupRow
}

export function canEditCallableType(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { node } = params
  const isGroupRow = !!node.group

  const rowData = stagingRowDataGetter(params)

  if (!rowData) {
    return false
  }

  if (isGroupRow && rowData.collection?.type !== CollectionType.ALL_OR_NOTHING) {
    return false
  }

  return !rowData.order.negotiationInstruction || rowData.order.negotiationInstruction.clearingType === null
}

export function canEditOrderDetailBondPrice(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params
  if (!data || !isOrderDetail(data)) {
    return false
  }
  return (
    !!data.order.collateralId &&
    !orderHasNonCommonCounterpartyInstructionValueSet(data.order, 'repoType') &&
    data.productSector?.basketType !== CollateralBasketType.GC_PLUS
  )
}

function getOrderNotionalEditable(data: OrderDetail | OrderDetailSummary) {
  if (!isOrderDetail(data)) {
    return false
  }

  const instrument = data.collateral ?? data.collateralBasket

  return instrument?.tradingType !== TradingType.CASH || data.productSector?.basketType === CollateralBasketType.DBV
}

export function canEditOrderDetailNotional(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params

  return !!data && getOrderNotionalEditable(data) && !hasOrderNestedLegs(params.data)
}

export function canEditOrderPreTradeAssignmentNotional(params: EditableCallbackParams<NestedLegItem>) {
  if (!makeCanEditPreTradeAssignmentDetailField(NegotiationFieldMeta.NOTIONAL)(params)) {
    return false
  }

  const orderDetail = getOrderDetailFromAgGridContext(params)

  return orderDetail?.order?.tradingType !== TradingType.CASH
}

function getOrderStartConsiderationEditable(data: OrderDetail | OrderDetailSummary) {
  if (!isOrderDetail(data)) {
    return false
  }

  const instrument = data.collateral ?? data.collateralBasket

  return (
    !!instrument &&
    instrument.tradingType !== TradingType.NOTIONAL &&
    data.productSector?.basketType !== CollateralBasketType.DBV &&
    !orderHasNonCommonCounterpartyInstructionValueSet(data.order, 'repoType')
  )
}

export function canEditOrderDetailStartConsideration(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params

  return !!data && getOrderStartConsiderationEditable(data) && !hasOrderNestedLegs(data)
}

export function canEditOrderPreTradeAssignmentStartConsideration(params: EditableCallbackParams<NestedLegItem>) {
  if (!makeCanEditPreTradeAssignmentDetailField(NegotiationFieldMeta.START_CONSIDERATION)(params)) {
    return false
  }

  const orderDetail = getOrderDetailFromAgGridContext(params)

  return orderDetail?.order?.tradingType !== TradingType.NOTIONAL
}

export function canEditOrderDetailRepoType(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const detail = stagingRowDataGetter(params)

  const order = detail?.order ?? null

  return (
    !isSmartGroupCollection(detail?.collection) &&
    !orderHasNonCommonCounterpartyInstructionValueSet(order, 'repoType') &&
    isChildRow(params) &&
    !hasOrderNestedLegs(detail)
  )
}

export function hasOrderNestedLegs(orderDetail: OrderDetail | OrderDetailSummary | null | undefined) {
  if (orderDetail && isOrderDetail(orderDetail)) {
    return !!orderDetail?.order?.nestedLegs?.legs?.length
  }

  return false
}

export function canEditOrderDetailCollateralName(
  params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>,
  disableOrderFix?: boolean
) {
  const detail = stagingRowDataGetter(params)

  if (disableOrderFix) {
    return false
  }
  if (isSmartGroupOrderDetailSummary(detail)) {
    return true
  } else if (isSmartGroupOrderDetail(detail)) {
    return false
  }

  return isChildRow(params) && !hasOrderNestedLegs(detail)
}

export function canEditOrderDetailDirection(
  params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>,
  disableOrderFix?: boolean
) {
  const detail = stagingRowDataGetter(params)

  if (disableOrderFix) {
    return false
  }
  if (isSmartGroupOrderDetailSummary(detail)) {
    return true
  } else if (isSmartGroupOrderDetail(detail)) {
    return false
  }

  return isChildRow(params)
}

export function canEditOrderTerm(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const detail = stagingRowDataGetter(params)

  if (isSmartGroupOrderDetailSummary(detail)) {
    return true
  } else if (isSmartGroupOrderDetail(detail)) {
    return false
  }

  return isChildRow(params)
}

export function canEditEvergreenNotice(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  return isChildRow(params)
}

export function canEditOrderDetailStartDate(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const detail = stagingRowDataGetter(params)

  if (isSmartGroupOrderDetailSummary(detail)) {
    return true
  } else if (isSmartGroupOrderDetail(detail)) {
    return false
  }

  return isChildRow(params)
}

export function canEditOrderDetailEndDate(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const detail = stagingRowDataGetter(params)

  if (isSmartGroupOrderDetailSummary(detail)) {
    return true
  } else if (isSmartGroupOrderDetail(detail)) {
    return false
  }

  return isChildRow(params)
}

function getOrderStartConsiderationOverrideEditable(data: OrderDetail | OrderDetailSummary) {
  if (!isOrderDetail(data)) {
    return false
  }

  return data.productSector?.startConsiderationOverrideThreshold !== 0 && !data.collateralBasket && getOrderStartConsiderationEditable(data)
}

export function canEditOrderDetailStartConsiderationOverride(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params

  if (!data || !isOrderDetail(data)) {
    return false
  }

  return getOrderStartConsiderationOverrideEditable(data) && !hasOrderNestedLegs(data)
}

export function canEditOrderPreTradeAssignmentStartConsiderationOverride(params: EditableCallbackParams<NestedLegItem>) {
  if (!makeCanEditPreTradeAssignmentDetailField(NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE)(params)) {
    return false
  }

  const orderDetail = getOrderDetailFromAgGridContext(params)

  return (
    orderDetail.productSector?.startConsiderationOverrideThreshold !== 0 &&
    !orderDetail.collateralBasket &&
    orderDetail.order.tradingType !== TradingType.NOTIONAL
  )
}

export function canEditOrderDetailCounterpartyName(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params

  if (!data || !isOrderDetail(data)) {
    return false
  }

  // BQ-3492 temporarily disable selection for AON items
  if (isAonOrOcoCollection(data?.collection?.type)) {
    return false
  }

  if (data?.counterparty) {
    return TradingEntityWithAliasHelper.getName(data.counterparty) !== Locale.translate(Translation.SC_MULTIPLE)
  } else {
    return true
  }
}

export function canEditOrderDetailFundId(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params

  return isChildRow(params) && !hasOrderNestedLegs(data)
}

export function canEditHaircut(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const order = stagingRowDataGetter(params) ?? null
  const settlementVenue = order?.order?.negotiationInstruction?.settlementVenue

  if (invalidHaircutScenario(order?.productSector?.basketType, settlementVenue)) return false

  if (order?.order.direction === SummaryRowDirection.MULTIPLE) {
    return false
  }
  return isChildRow(params) || order?.collection?.type === CollectionType.ALL_OR_NOTHING
}

export function invalidHaircutScenario(
  basketType: CollateralBasketType | null | undefined,
  settlementVenue: SettlementVenue | null | undefined
) {
  if (basketType === CollateralBasketType.GC_BESPOKE || basketType === CollateralBasketType.GC_PLUS) {
    return true
  }

  if (settlementVenue === SettlementVenue.Direct || settlementVenue === SettlementVenue.Sponsored) {
    return true
  }

  return false
}

export function canEditOrderDetailBookStrategy(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params

  return isChildRow(params) && !hasOrderNestedLegs(data)
}

export function canEditOrderBlockTrade(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params

  if (!data || !isOrderDetail(data)) {
    return false
  }
  return !!(data.orderFund && !data?.order?.nestedLegs?.legs && TradingEntityTypeUtils.isAssignType(data.orderFund))
}

export function canEditOrderIfNotAonOrOco(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params

  if (!data || !isOrderDetail(data)) {
    return false
  }
  return !data.collection || !isAonOrOcoCollection(data.collection.type)
}

export function canEditOrderTargetFillNotional(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params

  if (!data || !isOrderDetail(data)) {
    return false
  }

  return data.order.allowPartial && data.order.tradingType !== TradingType.CASH
}

export function canEditOrderTargetFillStartConsideration(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params

  if (!data || !isOrderDetail(data)) {
    return false
  }

  return (
    data.order.allowPartial &&
    data.order.tradingType !== TradingType.NOTIONAL &&
    !orderHasNonCommonCounterpartyInstructionValueSet(data.order, 'repoType')
  )
}

export function canEditSecondaryCollateral(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params

  if (!data || !isOrderDetail(data)) {
    return false
  }

  const basketType = data.productSector?.basketType

  return (isNil(basketType) || basketType === CollateralBasketType.GC_BESPOKE) && data.order.repoType === RepoType.STOCK_BORROWING_LENDING
}

export function canEditOrderFee(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params

  if (!data || !isOrderDetail(data)) {
    return false
  }

  return data.order.repoType === RepoType.STOCK_BORROWING_LENDING
}

export function canEditOrderRate(params: TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>) {
  const { data } = params

  if (!data || !isOrderDetail(data)) {
    return false
  }

  return data.order.repoType !== RepoType.STOCK_BORROWING_LENDING
}

export const orderHasNonCommonCounterpartyInstructionValueSet = <K extends keyof CounterpartyInstruction>(
  order: Order | OrderSummary | null,
  field: K
) => {
  if (order && isOrderSummary(order)) {
    return false
  }

  const counterpartyInstructions = order?.negotiationInstruction.counterpartyInstructions

  if (
    !counterpartyInstructions ||
    counterpartyInstructions.length === 0 ||
    counterpartyInstructions.every(counterpartyInstruction => counterpartyInstruction[field] === null)
  ) {
    return false
  }

  return !counterpartyInstructions.every(counterpartyInstruction => counterpartyInstruction[field] === counterpartyInstructions[0][field])
}

/* Misc */

export const getNotesFieldNameForDisplay = (fieldName: string) => {
  if (isEmpty(fieldName) || !fieldName.startsWith('notes')) {
    return null
  }

  const fieldNameParts = startCase(fieldName).split(' ')

  if (fieldNameParts[1]) {
    return Locale.translate(Translation.TC_NOTE, { noteIndex: parseInt(fieldNameParts[1], 10) + 1 })
  }

  return null
}

export function getOrderDetailFromAgGridContext<T extends { context: any }>(params: T): OrderDetail {
  if (!params.context.orderDetail) {
    throw new Error('orderDetail is missing on detail grid context')
  } else {
    const orderDetail = params.context.orderDetail as OrderDetail

    return orderDetail
  }
}
